/**
 * Samples domain — cache invalidation registry.
 *
 * Declares which queries every wrapped samples mutation invalidates on
 * success. Consumed by `hooks.ts` via `useInvalidatingMutation`. The
 * compiler enforces that every `SamplesMutationId` member has a registry
 * entry (and vice-versa), so adding a mutation without declaring its
 * invalidations is a build error.
 */

import type { QueryKey } from '@tanstack/react-query'

import { SAMPLES_TABLE_KEY } from '../../constants/tableQueryKeys'
import {
  containsKeyFilter,
  type InvalidationRule,
} from '../invalidation/useInvalidatingMutation'
import {
  getSampleRequestQueryKey,
  listSampleRequestsQueryKey,
} from '../generated/hooks/samplesController'

// ── Mutation identifiers ────────────────────────────────────────────

/**
 * Discriminator union of every wrapped samples mutation. Adding a new
 * mutation requires a member here and a corresponding entry in
 * `invalidationRegistry`.
 */
export type SamplesMutationId =
  | 'createSampleRequest'
  | 'updateSampleRequest'
  | 'cancelSampleRequest'
  | 'reviewSampleRequest'
  | 'updateSampleFulfillment'
  | 'exportSampleRequestsV2'

// ── Shared invalidation set ─────────────────────────────────────────

/**
 * Query keys every samples mutation should invalidate.
 *
 * Covers the generated list endpoint plus the two hand-written list
 * caches still keyed by string: `['samples-infinite', …]` (mobile
 * infinite scroll) and `['samples-counts', …]` (tab badge counts).
 * `invalidateQueries` does prefix matching, so the bare string roots
 * match every params variant.
 */
export function commonSamplesInvalidations(): QueryKey[] {
  return [
    listSampleRequestsQueryKey(),
    ['samples-infinite'],
    ['samples-counts'],
  ]
}

// ── Registry ────────────────────────────────────────────────────────

export const invalidationRegistry: Record<
  SamplesMutationId,
  InvalidationRule<any>
> = {
  createSampleRequest: {
    invalidates: () => commonSamplesInvalidations(),
    removes: () => [containsKeyFilter(SAMPLES_TABLE_KEY)],
  },
  updateSampleRequest: {
    invalidates: (variables: { id: number }) => [
      ...commonSamplesInvalidations(),
      getSampleRequestQueryKey({ id: variables.id }),
    ],
    removes: () => [containsKeyFilter(SAMPLES_TABLE_KEY)],
  },
  cancelSampleRequest: {
    invalidates: (variables: { id: number }) => [
      ...commonSamplesInvalidations(),
      getSampleRequestQueryKey({ id: variables.id }),
    ],
    removes: () => [containsKeyFilter(SAMPLES_TABLE_KEY)],
  },
  reviewSampleRequest: {
    invalidates: (variables: { id: number }) => [
      ...commonSamplesInvalidations(),
      getSampleRequestQueryKey({ id: variables.id }),
    ],
    removes: () => [containsKeyFilter(SAMPLES_TABLE_KEY)],
  },
  updateSampleFulfillment: {
    invalidates: (variables: { id: number }) => [
      ...commonSamplesInvalidations(),
      getSampleRequestQueryKey({ id: variables.id }),
    ],
    removes: () => [containsKeyFilter(SAMPLES_TABLE_KEY)],
  },
  // Export starts an async job; it doesn't mutate sample-request data.
  exportSampleRequestsV2: {
    invalidates: () => [],
  },
}
